import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const BUCKETS = [
  { k: "available", label: "Tersedia" },
  { k: "quarantine", label: "Karantina" },
  { k: "rejected", label: "Ditolak" },
  { k: "near_expiry", label: "Mendekati ED" },
  { k: "expired", label: "Kedaluwarsa" },
  { k: "all", label: "Semua Lot" },
];

export default function Stock() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [bucket, setBucket] = useState(params.get("b") || "available");
  const [rows, setRows] = useState([]);
  const [adj, setAdj] = useState(null);

  async function load() {
    const q = bucket === "all" ? {} : { bucket };
    const { data } = await api.get("/stock", { params: q });
    setRows(data);
  }
  useEffect(() => { load(); }, [bucket]);

  return (
    <div className="space-y-5">
      <div><h1 className="font-heading text-2xl font-extrabold">Stok Gudang</h1>
        <p className="text-sm text-muted-foreground">Pengendalian stok FIFO berdasarkan tanggal penerimaan &amp; kedaluwarsa.</p></div>
      <div className="flex flex-wrap gap-2">
        {BUCKETS.map((b) => (
          <button key={b.k} data-testid={`stock-tab-${b.k}`} onClick={() => setBucket(b.k)}
            className={`text-sm px-3 py-1.5 rounded-full border font-medium ${bucket === b.k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-accent"}`}>{b.label}</button>
        ))}
      </div>
      <Card className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b">
            <th className="py-2 font-medium">Batch Internal</th><th className="font-medium">Bahan</th><th className="font-medium">Tgl Terima</th>
            <th className="font-medium">ED</th><th className="font-medium">Lokasi</th><th className="font-medium">Sisa</th><th className="font-medium">Status</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.id} data-testid={`stock-row-${l.internal_batch}`} className={`border-b border-slate-100 dark:border-slate-800 ${l.expired ? "bg-red-50" : l.near_expiry ? "bg-orange-50" : ""}`}>
                <td className="py-2 cursor-pointer" onClick={() => nav(`/traceability?batch=${l.internal_batch}`)}><span className="batch-chip">{l.internal_batch}</span></td>
                <td>{l.material_name}</td><td>{fmtDate(l.tgl_terima)}</td>
                <td className={l.near_expiry || l.expired ? "text-orange-600 font-medium" : ""}>{fmtDate(l.expiry_date)}</td>
                <td>{l.location || "-"}</td><td className="font-medium">{l.qty_remaining} {l.unit}</td>
                <td><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{l.status}</span></td>
                <td><Button size="sm" variant="ghost" data-testid={`stock-adjust-${l.internal_batch}`} onClick={() => setAdj(l)}>Koreksi</Button></td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-muted-foreground">Tidak ada lot stok.</td></tr>}
          </tbody>
        </table>
      </Card>
      <AdjustDialog lot={adj} onClose={() => setAdj(null)} onDone={load} />
    </div>
  );
}

function AdjustDialog({ lot, onClose, onDone }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  if (!lot) return null;
  async function save() {
    try { await api.post("/stock/adjust", { lot_id: lot.id, qty_delta: parseFloat(delta), reason }); toast.success("Koreksi stok tersimpan."); onClose(); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  return (
    <Dialog open={!!lot} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>Koreksi Stok — {lot.internal_batch}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Sisa saat ini: {lot.qty_remaining} {lot.unit}. Koreksi memerlukan alasan &amp; tercatat di audit.</p>
        <div><Label className="text-xs">Perubahan (+/-)</Label><Input data-testid="adjust-delta" type="number" step="any" value={delta} onChange={(e) => setDelta(e.target.value)} className="mt-1" /></div>
        <div><Label className="text-xs">Alasan *</Label><Input data-testid="adjust-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" /></div>
        <DialogFooter><Button data-testid="adjust-save" onClick={save}>Simpan Koreksi</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
